import Alpine from 'alpinejs';
import serialize from 'form-serialize';

const defaults = {
  addToCartButton: '.js-add-to-cart',
  cartDrawer: '#cart-drawer',
  cartDrawerBar: '#cart-drawer-bar',
  cartDrawerItems: '#cart-drawer-items',
  cartDrawerFooter: '#cart-drawer-footer',
  cartDrawerOverlay: '.cart-drawer-overlay',
  cartDrawerTrigger: '.js-cart-drawer-trigger',
  cartDrawerClose: '.js-cart-drawer-close',
  cartDrawerCartJson: '#ajax-cart-drawer-json',
  cartCount: '.cart-count',
  lineItem: '.cart-line-item',
  lineItemUpdate: '.js-cart-line-item-update',
  lineItemQuantity: '.js-cart-line-item-input-quantity',
  moneyFormat: Shopify.currency.default_money_format,
};

class CartUtils {
  static removeItemAnimation(item) {
    item.classList.add('is-invisible');
  }

  static openCartDrawer() {
    document.documentElement.style.overflow = 'hidden';
    Alpine.store('cart').drawerIsOpen = true;
    this.openCartOverlay();
  }

  static closeCartDrawer() {
    document.documentElement.style.overflow = 'unset';
    Alpine.store('cart').drawerIsOpen = false;
    this.closeCartOverlay();
  }

  static openCartOverlay() {
    document.querySelector(defaults.cartDrawerOverlay).classList.add('is-open');
  }

  static closeCartOverlay() {
    document.querySelector(defaults.cartDrawerOverlay).classList.remove('is-open');
  }

  static updateCartItemCount(cart) {
    if (cart.item_count == 0) {
      document.body.classList.add('cart-is-empty');
    } else {
      document.body.classList.remove('cart-is-empty');
    }

    document.querySelectorAll(defaults.cartCount).forEach((itemCount) => {
      itemCount.innerHTML = cart.item_count;
    });
  }

  static setLoadingButton(button, isLoading = true) {
    if (isLoading) {
      button.disabled = true;
      button.classList.add('processing-spinner');
    } else {
      button.disabled = false;
      button.classList.remove('processing-spinner');
    }
  }

  static updateSections(response) {
    if (!response.sections) return;
    const sections = response.sections;

    if (sections['cart-drawer']) {
      const sectionCartDrawerText = sections['cart-drawer'].replace('loading="lazy"', 'loading="eager"');
      const drawerResponse = new DOMParser().parseFromString(sectionCartDrawerText, 'text/html');
      const drawerItems = document.querySelector(defaults.cartDrawerItems);
      drawerItems.innerHTML = drawerResponse.querySelector(defaults.cartDrawerItems).innerHTML;

      const drawerFooter = document.querySelector(defaults.cartDrawerFooter);
      if (drawerFooter != null) {
        drawerFooter.innerHTML = drawerResponse.querySelector(defaults.cartDrawerFooter).innerHTML;
      }
    }

    if (sections['cart']) {
      const cartSectionResponse = new DOMParser().parseFromString(sections['cart'], 'text/html');
      const cartSectionContainer = document.getElementById('shopify-section-cart');
      cartSectionContainer.innerHTML = cartSectionResponse.getElementById('shopify-section-cart').innerHTML;
    }
  }
}

const STOCK_CHECK_QUERY = `query checkStock($ids: [ID!]!) {
  nodes(ids: $ids) {
    ... on ProductVariant {
      id
      availableForSale
      quantityAvailable
    }
  }
}
`;
Alpine.store('cart', {
  cart: Shopify.cart || {},
  threshold: Number(document.querySelector('#free_product_threshold').dataset.threshold),
  freeProductVariantId: document.querySelector('#free_product_threshold').dataset.freeProductId,
  cartIsUpdating: false,
  drawerIsOpen: false,
  init() {
    this.initClickListeners();
    this.initEventListeners();

    Alpine.effect(() => {
      CartUtils.updateCartItemCount(this.cart);
      this.checkFreeProduct();
      fetch('/cart.js')
        .then((response) => response.json())
        .then((data) => {
          if (data.item_count == 0) {
            document.querySelectorAll('.cart-count-container').forEach((container) => {
              container.classList.add('hidden');
            });
          } else {
            document.querySelectorAll('.cart-count-container').forEach((container) => {
              container.classList.remove('hidden');
            });
          }
          document.querySelectorAll('.cart-count').forEach((itemCount) => {
            itemCount.innerHTML = data.item_count;
          });
        });
    });
  },

  async checkFreeProduct() {
    //Check for FREE PRODUCT PROMO, add product if qualified
    const thresholdProductVariantId = this.freeProductVariantId;
    const ids = `gid://shopify/ProductVariant/${thresholdProductVariantId}`;
    const cartTotal = this.cart.items_subtotal_price / 100;

    if (!thresholdProductVariantId || !this.threshold || this.threshold == 0) return;
    const stockData = await window.fetchStoreFront({
      query: STOCK_CHECK_QUERY,
      variables: { ids },
    });
    if (stockData && stockData.nodes) {
      const cartItem = this.cart.items.find((item) => item.variant_id == thresholdProductVariantId);
      if (
        stockData.nodes[0].availableForSale &&
        stockData.nodes[0].quantityAvailable > (cartItem ? cartItem.quantity : 0)
      ) {
        if (cartTotal >= this.threshold) {
          // Add the product if the threshold is reached
          const productInCart = this.cart.items.find((item) => item.variant_id == thresholdProductVariantId);
          if (!productInCart) {
            console.log('Threshold reached. Adding product...');
            let response = await this.addJS({
              id: thresholdProductVariantId,
              sections: ['cart-drawer'],
              quantity: 1,
            });

            CartUtils.updateSections(response);
            this.updateCartJson(response);
          }
        }
      }
    }
  },

  async addJS(formData) {
    this.cartIsUpdating = true;
    return fetch(Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
      .then((response) => {
        if (response.status == 422) {
          alert('Variant is out of stock');
        } else if (response.status != 200) {
          alert('An error has occurred! Please try again.');
        }

        if (response.status != 200) {
          throw new Error();
        }

        return response.json();
      })
      .catch((e) => {
        throw new Error(e);
      })
      .finally(() => {
        this.cartIsUpdating = false;
        // console.log(formData);
      });
  },

  async checkQuantities(event) {
    event.preventDefault();

    const cartItems = this.cart.items || [];
    // console.log('Cart Items:', cartItems);
    const ids = cartItems.map((item) => `gid://shopify/ProductVariant/${item.variant_id}`);

    // Fetch stock status
    try {
      const stockData = await window.fetchStoreFront({
        query: STOCK_CHECK_QUERY,
        variables: { ids },
      });
      // console.log('Stock Data:', stockData);
      if (stockData && stockData.nodes) {
        // console.log(stockData);
        // console.log(stockData.nodes[0]);
        const outOfStockItems = [];

        stockData.nodes.forEach((node, index) => {
          const cartItem = cartItems[index];
          if (!node.availableForSale || node.quantityAvailable < cartItem.quantity) {
            outOfStockItems.push(cartItem);
          }
        });

        if (outOfStockItems.length > 0) {
          // Remove out-of-stock items from the cart
          // this.cart.items = cartItems.filter((item) => !outOfStockItems.includes(item));
          // console.log('Updated Cart Items:', this.cart.items);

          // Notify the user about the items that were removed
          document.querySelector('.oos-message').classList.remove('hidden');
          outOfStockItems.forEach((item) => {
            let removeBtn = document.querySelector('.cart-line-item button[data-sku="' + item.variant_id + '"]');
            if (!removeBtn) {
              removeBtn = document.querySelector('.cart-line-item button[data-sku=""]');
            }
            if (removeBtn) {
              removeBtn.querySelector('.oos-span').classList.remove('hidden');
            }
            setTimeout(() => {
              let errorMsg = document.querySelector('.oos-message');
              // errorMsg.textContent = `El producto ${item.product_title} ya no se encuentra disponible y ha sido eliminado de su carrito.`;
              errorMsg.textContent = `Elimina los productos sin stock del carrito y continúa al pago.`;
              errorMsg.classList.remove('hidden');
            }, 100);
          });
        }

        const allInStock = stockData.nodes.every((node, index) => {
          const cartItem = cartItems[index];
          return node.availableForSale && node.quantityAvailable >= cartItem.quantity;
        });

        if (allInStock) {
          window.location.href = '/checkout';
        }
      } else {
        alert('No se encontró información del producto. Por favor, intente nuevamente.');
      }
    } catch (error) {
      console.error('Error checking stock:', error);
      alert('No se encontró información acerca del stock del producto. Por favor, intente nuevamente más tarde.');
    }
  },
  async changeJS(formData) {
    this.cartIsUpdating = true;
    return fetch(Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
      .then((response) => {
        if (response.status == 422) {
          alert('Variant is out of stock');
        } else if (response.status != 200) {
          alert('An error has occurred! Please try again.');
        }

        if (response.status != 200) {
          throw new Error();
        }

        return response.json();
      })
      .catch((e) => {
        throw new Error(e);
      })
      .finally(() => {
        this.cartIsUpdating = false;
      });
  },

  async addToCart(addToCartButton) {
    if (this.cartIsUpdating) return;

    let form = addToCartButton.closest('form');
    let formSerialize = serialize(form, { hash: true });
    const items = [formSerialize];

    let formData = {
      items,
      sections: ['cart-drawer'],
      sections_url: window.location.pathname + '?request_type=ajax',
    };

    //Check stock again before adding to cart
    const cartItems = this.cart.items || [];
    const variant_id = form.querySelector('.product-variant-id').value;
    const ids = `gid://shopify/ProductVariant/${variant_id}`;
    const stockData = await window.fetchStoreFront({
      query: STOCK_CHECK_QUERY,
      variables: { ids },
    });

    console.log('Stock Data:', stockData.nodes[0]);
    if (stockData && stockData.nodes && stockData.nodes.length > 0) {
      const node = stockData.nodes[0];
      const cartItem = cartItems.find((item) => item.variant_id == variant_id);
      const cartItemQty = cartItem ? cartItem.quantity : 0;
      if (!node.availableForSale || node.quantityAvailable <= cartItemQty) {
        // Create modal elements
        const modalOverlay = document.createElement('div');
        modalOverlay.classList.add('oos-modal-overlay');

        const modalContainer = document.createElement('div');
        modalContainer.classList.add('oos-modal-container');

        const closeButton = document.createElement('button');
        closeButton.classList.add('oos-modal-close');
        closeButton.innerHTML = '&times;';

        const modalMessage = document.createElement('div');
        modalMessage.classList.add('oos-modal-message');
        modalMessage.textContent = 'El producto seleccionado está agotado. Por favor, actualice la página';

        // Append elements
        modalContainer.appendChild(closeButton);
        modalContainer.appendChild(modalMessage);
        modalOverlay.appendChild(modalContainer);
        document.body.appendChild(modalOverlay);

        // Block scroll
        document.body.style.overflow = 'hidden';

        // Add click event to close the modal
        const closeModal = () => {
          document.body.removeChild(modalOverlay);
          document.body.style.overflow = '';
        };

        modalOverlay.addEventListener('click', closeModal);
        closeButton.addEventListener('click', closeModal);

        return;
      }
    }

    if (window.location.pathname.includes(Shopify.routes.cart_url)) formData.sections.push('cart');

    CartUtils.setLoadingButton(addToCartButton);
    let response = await this.addJS(formData).finally(() => {
      CartUtils.setLoadingButton(addToCartButton, false);
    });

    let mobileAtc = addToCartButton.classList.contains('button-mobile-atc') && window.innerWidth < 768;

    CartUtils.updateSections(response);
    if (!mobileAtc) {
      CartUtils.openCartDrawer();
    } else {
      let clickAwayTrigger = addToCartButton.closest('.click_away_trigger');
      clickAwayTrigger.querySelector('.click_away_trigger_inner').click();
      let productImgUrl = addToCartButton.getAttribute('product-img-url');
      let siteUrl = addToCartButton.getAttribute('site-url');
      let div = document.createElement('div');
      div.classList.add('mobile-atc-notification');
      div.innerHTML = `<img src="${siteUrl}/cdn/shop/${productImgUrl}" alt="Product image" class="w-[40px] h-[53px]"/>
      <p class="text-[12px] w-full">AGREGADO AL CARRITO!</p>
      <a href="javascript:void(0);" class="text-[12px] font-[900] text-nowrap open-cart-drawer">VER CARRITO</a>`;
      document.body.appendChild(div);
      console.log(siteUrl, productImgUrl);
      document.querySelector('.open-cart-drawer').addEventListener('click', function (event) {
        event.preventDefault();
        CartUtils.openCartDrawer();
      });
      setTimeout(() => {
        document.querySelector('.mobile-atc-notification').remove();
      }, 5000);
    }
    this.updateCartJson(response);
  },

  async updateCart(button) {
    if (this.cartIsUpdating) {
      console.log('Cart is already updating, please wait...');
      return;
    }

    this.cartIsUpdating = true;
    const type = button.dataset.type;
    const line = button.dataset.line;
    const lineItem = button.closest(defaults.lineItem);

    try {
      // Get initial cart state
      const initialCart = await fetch('/cart.js').then((r) => r.json());
      console.log('Initial cart state:', initialCart);

      const quantityInput = lineItem.querySelector(defaults.lineItemQuantity);
      const step = Number(quantityInput.step || 1);
      let quantity = Number(quantityInput.value || 0);

      switch (type) {
        case 'plus':
          quantity += step;
          break;
        case 'minus':
          quantity -= step;
          break;
        case 'remove':
          quantity = 0;
          break;
      }

      if (quantity === 0) {
        lineItem.classList.add('is-invisible'); // Direct class addition
        CartUtils.removeItemAnimation(lineItem);
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      let formData = {
        line: line,
        quantity: quantity,
        sections: ['cart-drawer'],
        sections_url: window.location.pathname + '?request_type=ajax',
      };

      if (window.location.pathname.includes(Shopify.routes.cart_url)) {
        formData.sections.push('cart');
      }

      const response = await this.changeJS(formData);
      CartUtils.updateSections(response);
      await this.updateCartJson(response);
    } catch (error) {
      console.error('Error updating cart:', error);
    } finally {
      this.cartIsUpdating = false;
    }
  },

  async getDrawerUpdated() {
    let endPoint = Shopify.routes.root + '?sections=cart-drawer';

    if (window.location.pathname.includes(Shopify.routes.cart_url)) endendPoint = endPoint + ',cart';

    let sections = await fetch(endPoint)
      .then((res) => {
        return res.json();
      })
      .catch((e) => {
        throw new Error(e);
      });

    const response = {
      sections: sections,
    };

    CartUtils.updateSections(response);
    CartUtils.openCartDrawer();
    this.updateCartJson(response);
  },

  async updateCartJson(response) {
    const responseHtml = new DOMParser().parseFromString(response.sections['cart-drawer'], 'text/html');
    this.cart = JSON.parse(responseHtml.querySelector(defaults.cartDrawerCartJson).textContent);
    window.cart_object = this.cart;
    document.dispatchEvent(new CustomEvent('cart:updated'));
  },

  initClickListeners() {
    document.addEventListener('click', (e) => {
      const target = e.target;

      if (target.closest(defaults.addToCartButton)) {
        e.preventDefault();
        this.addToCart(target.closest(defaults.addToCartButton));
      }

      if (target.closest(defaults.lineItemUpdate)) {
        this.updateCart(target.closest(defaults.lineItemUpdate));
      }

      if (target.closest(defaults.cartDrawerTrigger)) {
        e.preventDefault();
        CartUtils.openCartDrawer();
      }

      if (target.closest(defaults.cartDrawerOverlay) || target.closest(defaults.cartDrawerClose)) {
        CartUtils.closeCartDrawer();
      }
    });
  },

  initEventListeners() {
    window.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        if (document.querySelector(defaults.cartDrawer).classList.contains('is-open')) {
          CartUtils.closeCartDrawer();
        }
      }
    });
    window.cart_object = this.cart;
    document.dispatchEvent(new CustomEvent('cart:init', { detail: { cart: this.cart } }));
  },
});
